import { COMPONENT_STATE_ARGS, Metadata, pick } from '../../../utils/spds/metadata';
import {
  deactivate,
  isActive,
  isCapturable,
  isCloseKey,
  isDownKey,
  isHorizontalArrowKey,
  isLeftKey,
  isNavigable,
  isRightKey,
  isSelectionKey,
  isShiftableKey,
  isVerticalArrowKey,
  updateElementFocus,
} from '../../../utils/keyboard-nav-support';
import { indexInRange } from '../../../utils/spds/array-handling';
import { tracked } from '@glimmer/tracking';
import { waitForLater, waitForNext } from '../../../utils/waiters';
import Super from './base.gjs';

const MENU_OPEN_DELAY = 100;

const METADATA = Metadata.define({
  description: `
The keyboard-navigable base is an abstract core component that provides a reusable arrow-key navigation engine for any
SPDS component that exposes a list of focusable options behind an openable menu (search, dropdown list, filter buttons, and
future list/menu-style components). It does not render a template of its own; instead, components extend it and supply the
small set of configuration hooks described below.

### Navigation model

Navigation is built on the shared \`keyboard-nav-support\` utility. The currently focused option is tracked visually with the
CSS class \`active\` and programmatically via \`element.focus()\` (there is no roving \`tabindex="0"\` or \`aria-activedescendant\`).
Menu options set \`tabindex="-1"\` so they stay programmatically focusable without becoming sequential Tab stops.

The engine listens for \`keydown\` on the component's \`baseElement\` (registered by the base-class \`setupComponent\` modifier) and routes
keys as follows:

* When the menu is closed, \`ArrowUp\` / \`ArrowDown\` / \`Enter\` open it (when \`menuCanOpen\` allows). Opening with an arrow key also
  moves to the first/last option after a short delay so the menu has time to render.
* When the menu is open, \`ArrowUp\` / \`ArrowDown\` move the active option, \`ArrowLeft\` / \`ArrowRight\` traverse auxiliary controls
  when horizontal navigation is enabled (see below), \`Enter\` selects the active option, and \`Escape\` closes the menu and
  returns focus to the trigger through \`exitToTrigger\`, so focus is not lost with the option that was holding it.
* \`Tab\` / \`Shift+Tab\` never move within the list. They run \`exitOnTab\`, which marks the menu \`inert\`, calls \`exitToTrigger\` and
  \`handleClose\`, then lets the browser handle the key normally, so sequential focus leaves the component from its trigger.
  Nothing is selected on the way out. The \`inert\` step matters because the menu is not unmounted until after the browser has
  acted on the keydown; without it the native Tab could land on in-menu chrome that is on its way out. This is the behavior the
  APG [Menu and Menubar pattern](https://www.w3.org/WAI/ARIA/apg/patterns/menubar/) prescribes for composite widgets: Tab moves
  focus out of the menu and closes it rather than moving among its items.

### Horizontal navigation model

When \`allowHorizontalNavigation\` is enabled, the engine treats the component as an anchor with an arbitrarily-sized ordered list
of focusable controls on each side: \`leftNavigableOptions\` and \`rightNavigableOptions\` (index 0 nearest the anchor). The anchor
is dynamic via \`horizontalAnchor\` - the active menu option when horizontal navigation started from the menu, otherwise the
component's trigger/input (resolved through \`exitToTrigger\` when no anchor element exists).

* From the anchor, \`ArrowLeft\` enters \`leftNavigableOptions[0]\` and \`ArrowRight\` enters \`rightNavigableOptions[0]\`.
* Within a side, the arrow pointing away from the anchor moves outward and the opposite arrow moves inward; moving inward past
  index 0 returns focus to \`horizontalAnchor\`.
* At the far (outer) edge of a side the engine stops, unless \`wrapAroundHorizontal\` is enabled, in which case it wraps back to
  index 0 of that side.
* A horizontal key is only captured (preventing its default, e.g. caret movement) when there is somewhere to go and
  \`canEnterHorizontal(evt)\` allows it - input-based consumers override that hook to gate on caret position.

### navigableOptions registration contract

The engine never queries the DOM for options. Instead, a child component registers its option DOM nodes (via the base-class
component/element registration hooks) and the host registers that child as its \`optionSource\`. \`registeredOptions\` reads the
nodes from it, and \`navigableOptions\` narrows them to the ones that are actually visible. Movement, activation, and selection
all operate on that filtered array. In SPDS today every option source happens to be a menu, but the engine only requires a
component exposing \`navigableOptions\`.

The filter matters because a text filter (e.g. an in-menu \`@searchable\` field) only adds a \`hidden\` class to non-matching
options - they stay registered and stay in the DOM. Without it, arrow keys would land on options the user cannot see, and an
option hidden while still focused could be activated by \`Enter\`. Anything that has to touch every option regardless of
visibility (clearing \`active\` state on close, for instance) should read \`registeredOptions\` instead.

### Configurable surface

Inheriting components tune behavior by overriding these members:

* \`registeredOptions\` (getter): every registered option element, hidden ones included. Defaults to the registered
  \`optionSource\`'s options. Components that read \`navigableOptions\` during render (e.g. a trigger label) should override this
  with a snapshot populated in \`initialize\` to avoid backtracking re-renders.
* \`optionElement(option)\`: the element whose hidden state decides an option's visibility. Defaults to the option itself;
  override when the filter marks a wrapper instead (e.g. a checkbox inside a menu row).
* \`navigableOptions\` (getter): the ordered array of *visible* focusable option elements, derived from the two hooks above.
  Override directly only for a host that has no option source to register.
* \`allowHorizontalNavigation\` (getter): when \`true\`, \`ArrowLeft\` / \`ArrowRight\` drive the horizontal navigation model described
  above. Defaults to \`false\`.
* \`leftNavigableOptions\` / \`rightNavigableOptions\` (getters): the ordered focusable controls on each side of the anchor (index 0
  nearest the anchor). Default to empty arrays.
* \`horizontalAnchor\` (getter): the element horizontal navigation returns to when moving inward past index 0. Defaults to the
  current vertical active option; falls back to \`exitToTrigger\` when none exists.
* \`wrapAround\` (getter/field): when \`true\`, moving past the last option wraps to the first and moving before the first wraps to
  the last. When \`false\`, the engine calls \`exitToTrigger\` at the boundaries so a trigger-based menu can return focus to its
  trigger. Defaults to \`false\`.
* \`wrapAroundHorizontal\` (getter): the horizontal-axis counterpart of \`wrapAround\`. Defaults to \`false\` (stop at the outer edge).
* \`menuCanOpen\` (getter): gates keyboard opening. Defaults to \`true\`.
* \`getActiveOption\`: returns the element a selection key should act on. The default returns the active horizontal option (if
  any) then the active vertical option; trigger-based menus override this to fall back to the trigger.
* \`ownsKeyEvent(evt)\`: gates whether a key event belongs to the menu widget at all. Defaults to \`true\`. Override when the
  element the listener sits on also contains controls that are not part of the menu, so their keys reach them untouched -
  \`Spds::Action::Button::Split\` does this for its main action button. An override should still claim \`Escape\` and \`Tab\` while
  the menu is open, or those keys leave a menu open behind focus that has moved on.
* \`hasCustomAction(key)\`: gates whether a captured key performs a custom action. Defaults to \`true\`.
* \`canEnterHorizontal(evt)\`: gates whether a horizontal key is captured (e.g. only when an input's caret is at the edge).
  Defaults to \`true\`.
* \`optionSourceElement\` (getter): the option source's root element, sealed with \`inert\` while Tab leaves. Defaults to the
  registered \`optionSource\`'s \`baseElement\`, so a menu rendered with \`{{this.setupComponent}}\` needs no override.
* \`handleOpen\` / \`handleClose\`: open and close the menu. \`handleClose\` should clear \`active\` state from options. It may also
  decline to close by leaving \`open\` \`true\` (\`Spds::Input::MenuParent\` does this when its \`onClose\` returns \`false\`); the engine
  checks \`open\` afterwards and keeps focus inside the widget when the close was refused.
* \`exitToTrigger\`: returns focus to the component's trigger, a no-op by default. Called at arrow boundaries when
  \`wrapAround\` is \`false\`, and on \`Escape\` and \`Tab\` / \`Shift+Tab\` once the menu has actually closed. Any component with a
  trigger should override it, otherwise focus is left on whichever option was focused as the menu hides.
* \`initialize\`: optional async hook run once during setup (before the \`keydown\` listener is attached) for selection/value setup.

### Adopting in a new component

1. Extend this class and attach metadata via \`static metadata = this.setMetadata(METADATA, import.meta.url)\`.
2. Register the component that owns the options as \`optionSource\` so its options and root element resolve automatically.
3. Set \`allowHorizontalNavigation\` and \`wrapAround\` to match the desired behavior, override \`handleOpen\` / \`handleClose\` and
   \`exitToTrigger\` (required for Tab to leave the component cleanly), and expose \`leftNavigableOptions\` /
   \`rightNavigableOptions\` when the component has controls flanking the anchor.
4. Render the component's root element with \`{{this.setupComponent}}\` so \`baseElement\` is registered and the listener is wired up.
  `,
  extends: [pick(COMPONENT_STATE_ARGS, ['disabled'])],
  name: 'Keyboard Navigable',
});

/**
 * Abstract base that provides a configurable arrow-key navigation engine for menu-style SPDS components.
 *
 * @argument {boolean} disabled when true, all keyboard interaction is ignored
 *
 * @property {boolean} open whether the menu is currently open
 * @property {Element} activeOption the option element that currently has focus
 * @property {Element} activeHorizontalOption the left/right control that currently has focus, if any
 * @property {boolean} allowHorizontalNavigation enables ArrowLeft/ArrowRight handling (override)
 * @property {Array<Element>} leftNavigableOptions ordered controls to the left of the anchor (override)
 * @property {Array<Element>} rightNavigableOptions ordered controls to the right of the anchor (override)
 * @property {Element} horizontalAnchor element horizontal navigation returns to past index 0 (override)
 * @property {boolean} wrapAround wraps focus at list boundaries instead of exiting to a trigger (override)
 * @property {boolean} wrapAroundHorizontal wraps horizontal focus at the outer edge (override)
 * @property {object} optionSource the registered child component exposing navigableOptions
 * @property {Array<Element>} registeredOptions every registered option element (override to snapshot)
 * @property {Array<Element>} navigableOptions the visible subset of registeredOptions
 * @property {Element} optionSourceElement the source's root element, sealed with inert while Tab leaves
 * @property {boolean} menuCanOpen gates whether keyboard input may open the menu (override)
 */
export default class SpdsCoreKeyboardNavigable extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  @tracked activeOption;
  @tracked activeHorizontalOption;
  @tracked open = false;
  @tracked optionSource;

  /**
   * every registered option element, hidden ones included; read from the option source by default
   * override with a snapshot when navigableOptions is read during render
   * @return {Array<Element>}
   */
  get registeredOptions() {
    return this.optionSource?.navigableOptions ?? [];
  }

  /**
   * the visible option elements the engine navigates; a filter only adds a class, so the options
   * it hides stay registered and have to be dropped here to stay out of reach
   * @return {Array<Element>} the navigable option elements
   */
  get navigableOptions() {
    return this.registeredOptions.filter(option => isNavigable(this.optionElement(option)));
  }

  /**
   * the option source's root element, which the engine seals off while Tab leaves
   * @return {Element|undefined}
   */
  get optionSourceElement() {
    return this.optionSource?.baseElement;
  }

  /**
   * whether ArrowLeft/ArrowRight should be routed to horizontal navigation; override to enable
   * @return {boolean}
   */
  get allowHorizontalNavigation() {
    return false;
  }

  /**
   * the element horizontal navigation returns to when moving inward past index 0; defaults to the
   * active vertical option (falls back to exitToTrigger when undefined); override to customize
   * @return {Element|undefined}
   */
  get horizontalAnchor() {
    return this.activeOption;
  }

  /**
   * ordered controls to the left of the anchor (index 0 nearest the anchor); override to enable
   * @return {Array<Element>}
   */
  get leftNavigableOptions() {
    return [];
  }

  /**
   * gates whether keyboard input may open the menu; override to add conditions
   * @return {boolean}
   */
  get menuCanOpen() {
    return true;
  }

  /**
   * ordered controls to the right of the anchor (index 0 nearest the anchor); override to enable
   * @return {Array<Element>}
   */
  get rightNavigableOptions() {
    return [];
  }

  /**
   * whether focus wraps at list boundaries (true) or exits to a trigger (false); override to enable
   * @return {boolean}
   */
  get wrapAround() {
    return false;
  }

  /**
   * whether horizontal focus wraps at the outer edge of a side; override to enable
   * @return {boolean}
   */
  get wrapAroundHorizontal() {
    return false;
  }

  willDestroy() {
    super.willDestroy(...arguments);

    this.baseElement?.removeEventListener('keydown', this.keyboardNav);
  }

  /**
   * runs the optional async initialize hook, then attaches the keydown listener once
   * invoked by the base-class setupComponent modifier
   */
  setup = async () => {
    await this.initialize?.();

    this.baseElement.addEventListener('keydown', this.keyboardNav);
  };

  /**
   * determines whether a key pressed while closed should open the menu
   * @param {string} key the pressed key
   * @return {boolean}
   */
  canTriggerKeyboardOpen(key) {
    return (isVerticalArrowKey(key) || isSelectionKey(key)) && (this.menuCanOpen ?? true);
  }

  /**
   * resolves the movement direction from the event (ArrowDown = forward, ArrowUp = backward)
   * @param {KeyboardEvent} evt
   * @return {number} 1 for forward, -1 for backward
   */
  determineDirection({ key }) {
    return isDownKey(key) ? 1 : -1;
  }

  /**
   * returns the element a selection key should act on; override to add fallbacks (e.g. trigger)
   * @return {Element|undefined}
   */
  getActiveOption() {
    return this.activeHorizontalOption ?? this.navigableOptions?.find(isActive);
  }

  /**
   * gates whether a captured key performs a custom action; override to filter keys
   * @return {boolean}
   */
  hasCustomAction() {
    return true;
  }

  /**
   * gates whether a horizontal key is captured (preventing its default); override for input
   * consumers to require, e.g., the caret to be at the field edge
   * @return {boolean}
   */
  canEnterHorizontal() {
    return true;
  }

  /**
   * the element whose hidden state determines an option's visibility; override when the filter
   * marks a wrapper rather than the option itself (e.g. a checkbox inside a menu row)
   * @param {Element} option
   * @return {Element}
   */
  optionElement(option) {
    return option;
  }

  /**
   * opens the menu; override to run side effects (e.g. notifying consumers)
   */
  handleOpen = () => {
    this.open = true;
  };

  /**
   * closes the menu; override to clear active state from options and run side effects
   */
  handleClose = () => {
    this.open = false;
  };

  /**
   * whether a key event belongs to the menu widget; override when the element the listener sits on
   * also contains controls that are not part of it and must keep their own keyboard behavior
   * @return {boolean}
   */
  ownsKeyEvent() {
    return true;
  }

  /**
   * top-level keydown handler: opens the menu when closed, otherwise parses the action
   */
  keyboardNav = evt => {
    if (this.args.disabled || !this.ownsKeyEvent(evt)) return;

    if (!this.open) {
      this.openWithKeyboard(evt);
    } else {
      this.parseKeyAction(evt);
    }
  };

  /**
   * opens the menu in response to a triggering key and moves to an option once it has rendered
   * @param {KeyboardEvent} evt
   */
  async openWithKeyboard(evt) {
    if (!this.canTriggerKeyboardOpen(evt.key)) return;

    evt.preventDefault();
    this.handleOpen();

    if (!isSelectionKey(evt.key)) {
      // give the menu time to render before trying to move to an option
      await waitForLater(MENU_OPEN_DELAY);

      this.manageSelectedItem(evt);
    }
  }

  /**
   * routes a key pressed while the menu is open to selection, close, or movement
   * @param {KeyboardEvent} evt
   */
  parseKeyAction(evt) {
    let { key } = evt;

    if (!isCapturable(key)) return;
    if (isHorizontalArrowKey(key) && !this.canCaptureHorizontal(evt)) return;
    if (!this.hasCustomAction(key)) return;

    if (isShiftableKey(key)) {
      this.exitOnTab();
      return;
    }

    evt.preventDefault();

    if (isSelectionKey(key)) {
      this.selectActiveOption();
    } else if (isCloseKey(key)) {
      this.exitOnClose();
    } else {
      this.manageSelectedItem(evt);
    }
  }

  /**
   * Closes on Escape and returns focus to the trigger, which is what the APG prescribes: the menu
   * closes and focus goes back to the element it was opened from, rather than being lost to the
   * body along with the option that was holding it.
   *
   * `handleClose` is awaited first because a host can refuse to close - `Spds::Input::MenuParent`
   * keeps the menu open when its `onClose` returns `false`. Moving focus before knowing that would
   * pull it to the trigger of a menu that is still open.
   */
  async exitOnClose() {
    await this.handleClose();

    if (!this.open) this.exitToTrigger();
  }

  /**
   * Leaves the component on Tab, keeping the key's native behavior: focus returns to the trigger
   * so the browser resumes sequential navigation from the widget's own tab stop rather than from
   * a menu option, and the menu closes behind it. Nothing is selected on the way out.
   *
   * The menu is made `inert` first because `handleClose` does not unmount it until Ember's next
   * render, which is after the browser has already acted on this keydown. Without it the native
   * Tab can still walk into menu chrome that is on its way out - an in-menu search field, a
   * "Select all" button - and then lose focus entirely once the menu is destroyed. `inert` takes
   * the subtree out of focus navigation synchronously, so Tab resolves to the element after the
   * trigger.
   */
  async exitOnTab() {
    let source = this.optionSourceElement;

    if (source) source.inert = true;

    this.exitToTrigger();

    try {
      await this.handleClose();
      await waitForNext();
    } finally {
      // The menu element outlives a close, and `handleClose` may decline to close at all, so the
      // seal has to come off however this ends. A consumer `onClose` that throws would otherwise
      // leave the menu permanently unfocusable.
      if (source) source.inert = false;
    }

    // Sealing the menu and moving focus both bet on it closing. A host can refuse, and by then the
    // browser has already moved focus past the widget, so bring it back to the still-open menu's
    // trigger rather than stranding it downstream.
    if (this.open) this.exitToTrigger();
  }

  /**
   * activates the current selection; safe to call when nothing is active
   */
  selectActiveOption() {
    this.getActiveOption()?.click();
  }

  /**
   * routes movement keys to vertical or (when enabled) horizontal handling
   * @param {KeyboardEvent} evt
   */
  manageSelectedItem(evt) {
    if (isVerticalArrowKey(evt.key)) {
      this.handleVerticalPress(evt);
    } else if (this.allowHorizontalNavigation) {
      this.handleHorizontalPress(evt);
    }
  }

  /**
   * moves the active option up or down within navigableOptions; when a horizontal control is active,
   * a vertical key returns focus to the vertical active option instead of moving past it
   * @param {KeyboardEvent} evt
   */
  handleVerticalPress(evt) {
    let activeElement = this.getActiveOption();
    let direction = this.determineDirection(evt);
    let startingIndex = this.navigableOptions.findIndex(option => option === activeElement);

    if (indexInRange(this.navigableOptions.length, startingIndex)) {
      deactivate(this.navigableOptions[startingIndex]);
    }

    if (this.activeHorizontalOption) {
      this.deactivateHorizontalOption();
      this.updateVerticalSelection(
        this.navigableOptions.findIndex(option => option === this.activeOption)
      );
    } else {
      this.updateVerticalSelection(startingIndex + direction);
    }
  }

  /**
   * whether a horizontal key should be captured: horizontal navigation is enabled, the consumer
   * allows entering (e.g. caret at the edge), and there is somewhere to move
   * @param {KeyboardEvent} evt
   * @return {boolean}
   */
  canCaptureHorizontal(evt) {
    return (
      this.allowHorizontalNavigation &&
      this.canEnterHorizontal(evt) &&
      this.hasHorizontalDestination(evt)
    );
  }

  /**
   * whether there is a horizontal control to move to for the pressed key
   * @param {KeyboardEvent} evt
   * @return {boolean}
   */
  hasHorizontalDestination({ key }) {
    if (this.activeHorizontalOption) return true;

    let side = isLeftKey(key) ? this.leftNavigableOptions : this.rightNavigableOptions;

    return side.length > 0;
  }

  /**
   * traverses the left/right control lists: enters a side from the anchor, moves outward/inward
   * within it, returns to the anchor past index 0, and wraps or stops at the outer edge
   * @param {KeyboardEvent} evt
   */
  handleHorizontalPress({ key }) {
    let current = this.activeHorizontalOption;

    if (!current) {
      let side = isLeftKey(key) ? this.leftNavigableOptions : this.rightNavigableOptions;

      if (side.length) this.focusHorizontalOption(side[0]);

      return;
    }

    let onRight = this.rightNavigableOptions.includes(current);
    let side = onRight ? this.rightNavigableOptions : this.leftNavigableOptions;
    let movingOutward = onRight ? isRightKey(key) : isLeftKey(key);
    let nextIndex = side.indexOf(current) + (movingOutward ? 1 : -1);

    if (indexInRange(side.length, nextIndex)) {
      this.focusHorizontalOption(side[nextIndex]);
    } else if (nextIndex < 0) {
      this.returnToHorizontalAnchor();
    } else if (this.wrapAroundHorizontal) {
      this.focusHorizontalOption(side[0]);
    }
  }

  /**
   * focuses a horizontal control, clearing the previously active horizontal and vertical options
   * @param {Element} element
   */
  focusHorizontalOption(element) {
    deactivate(this.activeHorizontalOption);
    deactivate(this.activeOption);
    this.activeHorizontalOption = element;
    updateElementFocus(element);
  }

  /**
   * returns focus to the horizontal anchor (active option or, failing that, the trigger)
   */
  returnToHorizontalAnchor() {
    this.deactivateHorizontalOption();

    if (this.horizontalAnchor) {
      updateElementFocus(this.horizontalAnchor);
    } else {
      this.exitToTrigger();
    }
  }

  /**
   * clears the active horizontal control and its focus state
   */
  deactivateHorizontalOption() {
    deactivate(this.activeHorizontalOption);
    this.activeHorizontalOption = undefined;
  }

  /**
   * focuses the option at index, wrapping or exiting to the trigger at the boundaries
   * @param {number} index the target index (may be out of range)
   */
  updateVerticalSelection(index) {
    let length = this.navigableOptions.length;
    let lastIndex = length - 1;

    if (indexInRange(length, index)) {
      this.updateFocus(index);
      return;
    }

    if (this.wrapAround) {
      this.updateFocus(index < 0 ? lastIndex : 0);
    } else if (index < -1) {
      // moving up from the trigger lands on the last option
      this.updateFocus(lastIndex);
    } else {
      this.exitToTrigger();
    }
  }

  /**
   * returns focus to the component's trigger; used at arrow boundaries when not wrapping and
   * whenever Tab leaves the component; override in any component that has a trigger
   */
  exitToTrigger() {}

  /**
   * focuses the option at the given index and records it as the active option
   * @param {number} index
   */
  updateFocus(index) {
    this.activeOption = this.navigableOptions[index];
    updateElementFocus(this.activeOption);
  }
}

export const CORE_KEYBOARD_NAVIGABLE_METADATA = SpdsCoreKeyboardNavigable.metadata;
